import { PerformanceAudienceFilterOption, PerformanceAudienceSummary } from '@/services/endpoints/performanceAudience';
import { SelectOption } from '@/lib/Selects/Select/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterPillSelect } from '../FilterPillSelect';
import { FilterSummaryLine } from '../FilterSummaryLine';
import { AudienceFilterIcon, CalendarFilterIcon, GoogleAdsIcon, MetaIcon } from '../PlatformBrandIcons';
import { twMerge } from 'tailwind-merge';
import { findFilterOption } from '../../utils/filterOptions';

type PerformanceAudienceFilterCardProps = {
  isRefetching?: boolean;
  summary?: PerformanceAudienceSummary;
  audiences: PerformanceAudienceFilterOption[];
  googleScopes: PerformanceAudienceFilterOption[];
  metaScopes: PerformanceAudienceFilterOption[];
  dateRanges: PerformanceAudienceFilterOption[];
  selectedAudienceId: string;
  selectedGoogleScopeId: string;
  selectedMetaScopeId: string;
  selectedDateRangeId: string;
  onAudienceChange: (option: SelectOption) => void;
  onGoogleScopeChange: (option: SelectOption) => void;
  onMetaScopeChange: (option: SelectOption) => void;
  onDateRangeChange: (option: SelectOption) => void;
};

export const PerformanceAudienceFilterCard: FC<PerformanceAudienceFilterCardProps> = ({
  isRefetching = false,
  summary,
  audiences,
  googleScopes,
  metaScopes,
  dateRanges,
  selectedAudienceId,
  selectedGoogleScopeId,
  selectedMetaScopeId,
  selectedDateRangeId,
  onAudienceChange,
  onGoogleScopeChange,
  onMetaScopeChange,
  onDateRangeChange,
}) => {
  const { t } = useTranslation('performanceAudience');

  const selectedAudience = findFilterOption(audiences, selectedAudienceId);
  const selectedGoogleScope = findFilterOption(googleScopes, selectedGoogleScopeId);
  const selectedMetaScope = findFilterOption(metaScopes, selectedMetaScopeId);
  const selectedDateRange = findFilterOption(dateRanges, selectedDateRangeId);

  return (
    <section
      className={twMerge(
        'rounded-2xl border border-primary-border bg-white p-5 shadow-sm motion-safe:transition-opacity',
        isRefetching && 'pointer-events-none opacity-60'
      )}
      aria-busy={isRefetching}
    >
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
          <div className='flex flex-wrap gap-3'>
            <FilterPillSelect
              icon={<AudienceFilterIcon />}
              categoryLabel={t('filters.audience')}
              selected={selectedAudience}
              items={audiences}
              onChange={onAudienceChange}
              className='min-w-[12rem] max-w-full sm:max-w-xs'
            />
            <FilterPillSelect
              icon={<GoogleAdsIcon />}
              categoryLabel={t('filters.googleAds')}
              selected={selectedGoogleScope}
              items={googleScopes}
              onChange={onGoogleScopeChange}
              className='min-w-[14rem] max-w-full sm:max-w-sm'
            />
            <FilterPillSelect
              icon={<MetaIcon />}
              categoryLabel={t('filters.meta')}
              selected={selectedMetaScope}
              items={metaScopes}
              onChange={onMetaScopeChange}
              className='min-w-[12rem] max-w-full sm:max-w-xs'
            />
          </div>
          <FilterPillSelect
            icon={<CalendarFilterIcon />}
            selected={selectedDateRange}
            items={dateRanges}
            onChange={onDateRangeChange}
            variant='date'
            className='w-full shrink-0 sm:w-auto sm:min-w-[11rem]'
          />
        </div>
        {summary ? <FilterSummaryLine summary={summary} /> : null}
      </div>
    </section>
  );
};
